import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjects } from '../../hooks/useProjects';
import { projectsService } from '../../services/dataService';
import toast from 'react-hot-toast';
import { ArrowLeft, Plus, Edit2, Trash2, ExternalLink } from 'lucide-react';

const AdminProjects = () => {
  const navigate = useNavigate();
  const { projects, loading, error, refetch } = useProjects();
  const [deleting, setDeleting] = useState(null);

  const handleDelete = async (id, title) => {
    if (window.confirm(`Delete "${title}"? This cannot be undone.`)) {
      setDeleting(id);
      try {
        const result = await projectsService.delete(id);
        if (result.success) {
          toast.success('Project deleted successfully');
          refetch();
        } else {
          toast.error('Failed to delete project');
        }
      } catch (error) {
        toast.error('Error deleting project');
      } finally {
        setDeleting(null);
      }
    }
  };

   const getStatusColor = (status) => {
    switch (status) {
      case 'Live':
        return 'bg-white/20 text-white/90';
      case 'Demo':
        return 'bg-white/15 text-white/70';
      case 'In Progress':
        return 'bg-white/10 text-white/50';
      default:
        return 'bg-white/10 text-white/50';
    }
  };
  
  return (
    <div className="min-h-screen bg-black text-white font-mono">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/adminaccess/dashboard')}
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>

          <button
            onClick={() => navigate('/adminaccess/projects/new')}
            className="flex items-center gap-2 px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Add Project</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-light mb-2">Manage Projects</h1>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="p-8 rounded-lg border border-white/10 bg-white/5 text-center">
            <div className="inline-block w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin mb-4"></div>
            <p className="text-white/60">Loading projects...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="p-8 rounded-lg border border-red-500/20 bg-red-500/5 text-center">
            <p className="text-red-400">Error: {error}</p>
          </div>
        )}

        {/* Projects List */}
        {!loading && !error && (
          <div className="space-y-4">
            {projects.length === 0 ? (
              <div className="p-12 rounded-lg border border-white/10 bg-white/5 text-center">
                <p className="text-white/40 mb-4">No projects yet</p>
                <button
                  onClick={() => navigate('/adminaccess/projects/new')}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Your First Project</span>
                </button>
              </div>
            ) : (
              projects.map((project) => (
                <div
                  key={project.id}
                  className="p-4 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 transition-all"
                >
                  <div className="flex gap-4">
                    {/* Project Image */}
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-20 h-20 rounded-lg object-cover border border-white/10"
                    />

                    {/* Project Info */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-lg font-medium">{project.title}</h3>
                            <span
                              className={`text-xs px-2 py-1 rounded ${
                                project.status === 'Live'
                                  ? 'bg-green-500/20 text-white-400'
                                  : 'bg-white-500/20 text-red-400'
                              }`}
                            >
                              {project.status}
                            </span>
                          </div>
                          <a
                            href={`https://${project.url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-white/40 hover:text-white/60 transition-colors inline-flex items-center gap-1"
                          >
                            {project.url}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => navigate(`/adminaccess/projects/edit/${project.id}`)}
                            className="p-2 text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(project.id, project.title)}
                            disabled={deleting === project.id}
                            className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      <p className="text-sm text-white/60 mb-3 line-clamp-2">
                        {project.description}
                      </p>

                      {/* Tech Stack */}
                      <div className="flex flex-wrap gap-2">
                        {project.tech.map((tech, index) => (
                          <span
                            key={index}
                            className="text-xs px-2 py-1 rounded bg-white/10 text-white/50"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminProjects;